import SimpleCalendar from "lib/components/common/calendar/simple-calendar";
import ClinicsDropdown from "lib/components/common/dashboard/clinics-dropdown";
import ErrorBox from "lib/components/common/error-box";
import FlexBox from "lib/components/common/flex-box";
import SimpleButton from "lib/components/common/simple-button";
import SimpleCard from "lib/components/common/simple-card";
import SimpleDataTable from "lib/components/common/simple-data-table";
import SimpleDropdown from "lib/components/common/simple-dropdown";
import SimpleList from "lib/components/common/simple-list";
import SimpleModal from "lib/components/common/simple-modal";
import SimpleTitle from "lib/components/common/simple-title";
import RoomBookingSummaryTile from "lib/components/practitioners/room-booking-summary-tile";
import { EMPTY } from "lib/constants";
import { DEFAULT_PRACTITIONER_SUBSCRIPTION_DETAILS } from "lib/constants/default-values";
import { PRACTITIONER_SUBSCRIPTIONS_COLUMNS } from "lib/constants/table-columns";
import useFormBinder from "lib/hooks/useFormBinder";
import ROOM_BOOKING_SERVICE from "lib/services/room-booking-service";
import {
  getArrayOfValuesFromArray,
  getItemFromArray,
  mergeArrays,
} from "lib/utils/array-utils";
import { formatDate, getLocalISO, getTotalMonthsFromDates } from "lib/utils/date-utils";
import { isEmpty } from "lib/utils/string-utils";
import { formatRoomNumber } from "lib/utils/util";
import { FC, useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { PiPlusBold } from "react-icons/pi";
import { toast } from "sonner";

interface RoomBookingTabPageProps {
  clinicRooms?: RoomDetails[];
  clinics?: Clinic[];
  error?: string | null;
  isLoading?: boolean;
  practitionerId?: string;
  practitionerName?: string;
  //roomBookingDetails?: RoomBookingsDetails[];
  selectedClinicRoomId?: string;
  practitionerSubscriptions?: PractitionerSubscriptionDetails[];
  clinicSubscriptions?: PractitionerSubscriptionDetails[];
  unavailableDays: UnavailableDay[];
  sundayDisabled?: boolean;
  onCancelBooking?: (bookingId: string) => void;
  onClinicChange?: (clinicId: string) => void;
  onNewBooking?: (
    data: PractitionerSubscriptionDetails,
    onSuccess: VoidFunction
  ) => void;
  onRoomChange?: (roomId: string) => void;
  onSubscriptionClick?: (details: PractitionerSubscriptionDetails) => void;
}

const RoomBookingTabPage: FC<RoomBookingTabPageProps> = ({
  clinicRooms = [],
  clinics = [],
  error,
  isLoading,
  practitionerId = EMPTY,
  practitionerName = EMPTY,
  selectedClinicRoomId = EMPTY,
  practitionerSubscriptions = [],
  clinicSubscriptions = [],
  unavailableDays = [],
  sundayDisabled = false,
  onCancelBooking,
  onClinicChange,
  onNewBooking,
  onRoomChange,
  onSubscriptionClick,
}) => {
  const [selectedRoomBooking, setSelectedRoomBooking] =
    useState<PractitionerSubscriptionDetails | null>();
  
  const [isSundayDisabled, setIsSundayDisabled] = useState(sundayDisabled);

  const { values, setValues } = useFormBinder<PractitionerSubscriptionDetails>({
    ...DEFAULT_PRACTITIONER_SUBSCRIPTION_DETAILS(
      clinics.length > 0 ? clinics[0].id : "",
      clinicRooms.length > 0 ? clinicRooms[0].id : selectedClinicRoomId
    ),
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSelectedRoomBookingDatesChange = (dates: Date[]) => {
    //const dateStrings = dates.map((date) => new Date(date).toISOString());
    setValues({ ...values, internalDates: dates });
  };

  const handleSubmitClick = () => {
    setErrorMessage(null);
    if (isEmpty(values?.id)) {
      // Create new booking

      // Validate dates
      if (values?.internalDates?.length === 0) {
        setErrorMessage("Please select at least one date for the booking.");
        return;
      }

      // Fill in the missing fields
      values.dates = (values.internalDates ?? []).map((date) =>
        getLocalISO(new Date(date))
      );
      values.fromDate = values.dates[0];
      values.toDate = values.dates[values.dates.length - 1];
      values.practitionerId = practitionerId;
      values.roomId = selectedClinicRoomId;
      values.clinicId = selectedClinic?.id;
      values.clinicName = selectedClinic?.name;
      values.room = selectedClinicRoom?.roomNumber;
      values.perDayPrice = selectedClinicRoom?.pricePerDay;
      values.isActive = false;
      values.months = getTotalMonthsFromDates(values.internalDates);
      values.totalCost =
        values.perDayPrice * (values.internalDates ?? []).length;

      onNewBooking?.(values, () => setSelectedRoomBooking(null));

      toast.success("New booking created successfully!");
    } else {
      // Update existing booking
      toast.success("New booking updated successfully!");
    }
  };

  /** Calculated vars */

  const selectedClinic = clinics.find(
    (clinic) => clinic.id === values.clinicId
  );

  const roomValues = getArrayOfValuesFromArray(clinicRooms, "id");

  const roomOptions = getArrayOfValuesFromArray(clinicRooms, "roomNumber").map(
    (roomNumber) => formatRoomNumber(roomNumber)
  );

  const selectedClinicRoom = getItemFromArray(
    clinicRooms,
    "id",
    selectedClinicRoomId
  );

  //const selectedDates = values.dates?.map((date) => new Date(date));

  const unavailableDates = ROOM_BOOKING_SERVICE.getUnavailableRoomDates(
    clinicSubscriptions,
    selectedClinicRoomId
  );

  const practitionerRoomBookings =
    ROOM_BOOKING_SERVICE.getPractitionerRoomBookings(
      practitionerSubscriptions,
      practitionerId
    );

  const updatedUnavailableDates = mergeArrays(
    unavailableDays,
    unavailableDates
  );

  useEffect(() => {
    if (selectedClinic) {
      setIsSundayDisabled(selectedClinic.sundayClose ?? false);
    }
  }, [selectedClinic]);

  /** Calculated vars */

  return (
    <SimpleCard
      id="practitioner-subscriptions"
      isLoading={isLoading}
      error={error}
    >
      <SimpleDataTable
        title="Room Bookings"
        columns={PRACTITIONER_SUBSCRIPTIONS_COLUMNS}
        data={practitionerRoomBookings ?? []}
        onRowClick={(row) => {
          onSubscriptionClick?.(row as PractitionerSubscriptionDetails);
          console.log(row);
        }}
        isShowPagination={true}
        action={
          <SimpleButton
            onClick={() => {
              setSelectedRoomBooking({
                ...DEFAULT_PRACTITIONER_SUBSCRIPTION_DETAILS(
                  clinics.length > 0 ? clinics[0].id : "",
                  clinicRooms.length > 0
                    ? clinicRooms[0].id
                    : selectedClinicRoomId
                ),
              });
              setValues({
                ...DEFAULT_PRACTITIONER_SUBSCRIPTION_DETAILS(
                  clinics.length > 0 ? clinics[0].id : "",
                  clinicRooms.length > 0
                    ? clinicRooms[0].id
                    : selectedClinicRoomId
                ),
              });
            }}
          >
            <FlexBox>
              <>Add</> <PiPlusBold />
            </FlexBox>
          </SimpleButton>
        }
      />
      <SimpleModal
        open={!!selectedRoomBooking}
        close={() => setSelectedRoomBooking(null)}
        title={selectedRoomBooking?.id ? "Update Booking" : "New Booking"}
        size="lg"
        footer={
          <FlexBox justifyContent="between" className="">
            <ErrorBox message={error || errorMessage} />
            <SimpleButton
              onClick={handleSubmitClick}
              disabled={roomOptions?.length === 0}
            >
              Submit
            </SimpleButton>
          </FlexBox>
        }
      >
        <Row>
          <Col sm={6}>
            <ClinicsDropdown
              clinics={clinics}
              onSelect={(clinic) => {
                setValues({ ...values, clinicId: clinic.id });
                // Update sundayDisabled from selected clinic
                setIsSundayDisabled(clinic.sundayClose ?? false);
                onClinicChange?.(clinic.id);
              }}
              selectedClinic={selectedClinic}
              className="mb-3"
            />
            <SimpleDropdown
              options={
                roomOptions.length > 0 ? roomOptions : ["No Rooms Available"]
              } // Populate with room numbers
              values={roomValues} // Populate with room IDs
              label="Room Number"
              name="roomId"
              disabled={values.clinicId === EMPTY} // Disable if no clinic is selected
              selectedValue={values.roomId} // Set selected room ID
              onChange={(e) => {
                onRoomChange?.(e.target.value);
                setValues({
                  ...values,
                  roomId: e.target.value,
                  dates: [],
                  internalDates: [],
                });
              }}
            />
            <SimpleCalendar
              disabled={roomOptions?.length === 0 || !selectedClinicRoom.isActive} // Disable if no room is available or if room is inactive
              isNavigationDisabled={values.roomId === EMPTY} // Disable if no room is
              selectedDates={values.internalDates ?? []}
              selectedDate={null}
              unavailableDays={updatedUnavailableDates}
              mode="multiple"
              onSelectedDatesChange={handleSelectedRoomBookingDatesChange}
              disableSundays={isSundayDisabled}
            />
            <ul className="mt-3">
              <li>
                <small>
                  <i>To select a date click on the date.</i>
                </small>
              </li>
              <li>
                <small>
                  <i>To deselect the date click the date again</i>
                </small>
              </li>
            </ul>
          </Col>
          <Col sm={6}>
            <SimpleTitle title="Room Summary" className="mb-2" />
            <RoomBookingSummaryTile
              practitionerName={practitionerName}
              clinicName={selectedClinic?.name || "No Clinic Selected"}
              roomNumber={selectedClinicRoom?.roomNumber}
              dates={values.internalDates ?? []}
              pricePerDay={selectedClinicRoom?.pricePerDay} // Assuming a fixed price per day for simplicity
            />
            <SimpleTitle title="Selected Dates" />
            {values.internalDates?.length === 0 ? (
              <small className="text-muted">No dates selected yet</small>
            ) : (
              <SimpleList
                title="Selected Dates"
                items={values.internalDates?.map((date) =>
                  formatDate(getLocalISO(new Date(date)))
                )}
                className="auto-scroll-250"
              />
            )}
          </Col>
        </Row>
      </SimpleModal>
    </SimpleCard>
  );
};

export default RoomBookingTabPage;
